import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import Keyboard from '../../shared/ui/Keyboard';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import { isValidWordFull } from '../../shared/dictionary';
import { link, normalize, type NormalizedWordChain, type WordChainLevel } from './types';
import './wordChain.css';

export default function WordChainGame() {
  const [data, setData] = useState<{
    puzzle: NormalizedWordChain;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<WordChainLevel>('wordChain')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Word Chains yet.');
          return;
        }
        const puzzle = normalize(res.data);
        if (!puzzle) {
          setLoadError('Today\'s Word Chains puzzle is malformed.');
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="wordChain" />;
  if (!data) {
    return (
      <GameShell gameId="wordChain">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('wordChain', data.dateKey)) {
    return <FinishedDaily gameId="wordChain" dateKey={data.dateKey} isToday={data.isToday} />;
  }

  return (
    <WordChainPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

type RowState = 'given' | 'solved' | 'active' | 'pending';

interface Snapshot {
  solvedCount: number;
  /** Hinted letter positions, per word index. Position 0 is always free. */
  revealed: Record<number, number[]>;
  mistakes: number;
  hintUsed: boolean;
}

function WordChainPlay({
  puzzle,
  dateKey,
  isToday,
}: {
  puzzle: NormalizedWordChain;
  dateKey: string;
  isToday: boolean;
}) {
  const { restored, save } = useDailySnapshot<Snapshot>('wordChain', dateKey);

  // Word 0 is given, so a fresh board starts with one word already on it.
  const [solvedCount, setSolvedCount] = useState(() =>
    Math.min(Math.max(restored?.solvedCount ?? 1, 1), puzzle.words.length - 1),
  );
  const [revealed, setRevealed] = useState<Record<number, number[]>>(
    () => restored?.revealed ?? {},
  );
  const [mistakes, setMistakes] = useState(() => restored?.mistakes ?? 0);
  const [hintUsed, setHintUsed] = useState(() => restored?.hintUsed ?? false);
  const [typed, setTyped] = useState('');
  const [outcome, setOutcome] = useState<'won' | 'lost' | null>(null);
  const [shake, setShake] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  usePersistedState(
    save,
    { solvedCount, revealed, mistakes, hintUsed },
    outcome === null,
  );

  const activeIndex = outcome === null && solvedCount < puzzle.words.length ? solvedCount : null;
  const activeWord = activeIndex === null ? null : puzzle.words[activeIndex];

  const isRevealed = useCallback(
    (index: number, position: number) =>
      position === 0 || (revealed[index]?.includes(position) ?? false),
    [revealed],
  );

  /** Slots of the active word the player still has to fill, left to right. */
  const openSlots = useMemo(() => {
    if (activeIndex === null || !activeWord) return [];
    const out: number[] = [];
    for (let i = 0; i < activeWord.length; i++) {
      if (!isRevealed(activeIndex, i)) out.push(i);
    }
    return out;
  }, [activeIndex, activeWord, isRevealed]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1400);
  }, []);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 450);
  }, []);

  const solvedRef = useRef(false);
  useEffect(() => {
    if (outcome !== 'won' || solvedRef.current) return;
    solvedRef.current = true;
    recordOutcome('wordChain', dateKey, 'won', {
      subtitle:
        mistakes === 0
          ? `Chained all ${puzzle.words.length} words without a mistake.`
          : `Chained all ${puzzle.words.length} words with ${mistakes} ${
              mistakes === 1 ? 'mistake' : 'mistakes'
            }.`,
    });
  }, [outcome, dateKey, mistakes, puzzle.words.length]);

  const lostRef = useRef(false);
  useEffect(() => {
    if (outcome !== 'lost' || lostRef.current) return;
    lostRef.current = true;
    recordOutcome('wordChain', dateKey, 'lost', {
      subtitle: 'Out of lives.',
      answer: puzzle.words.join(' → '),
    });
  }, [outcome, dateKey, puzzle.words]);

  /** Fills the active word's slots: given letters in place, typed ones after. */
  const slotLetter = useCallback(
    (index: number, position: number): string => {
      const word = puzzle.words[index];
      if (isRevealed(index, position)) return word[position];
      const typedIdx = openSlots.indexOf(position);
      return typedIdx >= 0 && typedIdx < typed.length ? typed[typedIdx] : '';
    },
    [puzzle.words, isRevealed, openSlots, typed],
  );

  const advance = useCallback(() => {
    const next = solvedCount + 1;
    setSolvedCount(next);
    setTyped('');
    if (next >= puzzle.words.length) setOutcome('won');
  }, [solvedCount, puzzle.words.length]);

  // Guards the round against a second submit landing while the dictionary
  // lookup is in flight — a real word must never cost two lives.
  const submitting = useRef(false);

  const submit = useCallback(async () => {
    if (activeIndex === null || !activeWord || outcome !== null || submitting.current) return;
    if (typed.length !== openSlots.length) {
      triggerShake();
      showToast('Fill in all letters');
      return;
    }

    const guess = activeWord
      .split('')
      .map((_, i) => slotLetter(activeIndex, i))
      .join('');

    if (guess === activeWord) {
      advance();
      return;
    }

    // Only a real word costs a life — a typo gets a free retry, exactly as in
    // the app.
    submitting.current = true;
    try {
      const isWord = await isValidWordFull(guess);
      if (!isWord) {
        triggerShake();
        showToast('Not in word list');
        return;
      }
      const next = mistakes + 1;
      setMistakes(next);
      triggerShake();
      setTyped('');
      if (next >= puzzle.maxMistakes) setOutcome('lost');
    } finally {
      submitting.current = false;
    }
  }, [
    activeIndex,
    activeWord,
    outcome,
    typed.length,
    openSlots.length,
    triggerShake,
    showToast,
    slotLetter,
    advance,
    mistakes,
    puzzle.maxMistakes,
  ]);

  const onLetter = useCallback(
    (letter: string) => {
      if (activeIndex === null || outcome !== null) return;
      setTyped((prev) => (prev.length >= openSlots.length ? prev : prev + letter));
    },
    [activeIndex, outcome, openSlots.length],
  );

  const onDelete = useCallback(() => {
    if (outcome !== null) return;
    setTyped((prev) => prev.slice(0, -1));
  }, [outcome]);

  const useHint = useCallback(() => {
    if (activeIndex === null || !activeWord || outcome !== null || hintUsed) return;
    const position = openSlots[0];
    if (position === undefined) {
      showToast('No more letters to reveal');
      return;
    }
    setHintUsed(true);
    setRevealed((prev) => ({
      ...prev,
      [activeIndex]: [...(prev[activeIndex] ?? []), position],
    }));
    // Typed letters map onto the open slots in order, and the hint always fills
    // the leftmost one — so drop the first typed letter and the rest stay in
    // the slots the player put them in.
    setTyped((prev) => prev.slice(1));
    // Revealing the last open slot completes the word.
    if (openSlots.length === 1) advance();
  }, [activeIndex, activeWord, outcome, hintUsed, openSlots, showToast, advance]);

  const rowState = (index: number): RowState => {
    if (index === 0) return 'given';
    if (index < solvedCount) return 'solved';
    if (index === activeIndex) return 'active';
    return 'pending';
  };

  const livesLeft = Math.max(0, puzzle.maxMistakes - mistakes);

  const body = (
    <div className="wc-board">
      <div className="wc-status">
        {puzzle.category && <span className="wc-category">{puzzle.category}</span>}
        <span className="wc-lives" aria-label={`${livesLeft} lives left`}>
          {Array.from({ length: puzzle.maxMistakes }).map((_, i) => (
            <span key={i} className={'wc-life' + (i < livesLeft ? '' : ' is-spent')}>
              {i < livesLeft ? '♥' : '♡'}
            </span>
          ))}
        </span>
      </div>

      <div className="wc-chain">
        {puzzle.words.map((word, index) => {
          const state = rowState(index);
          const phrase = index > 0 && index < solvedCount ? link(puzzle.words, index - 1) : null;
          return (
            <div key={index} className="wc-step">
              {index > 0 && (
                <div className="wc-connector">
                  <span className="wc-connector-line" aria-hidden />
                  {phrase && <span className="wc-connector-phrase">{phrase}</span>}
                </div>
              )}
              <div
                className={
                  `wc-row is-${state}` + (state === 'active' && shake ? ' is-shake' : '')
                }
                style={{ ['--wc-len' as string]: String(word.length) }}
                aria-label={
                  state === 'pending' || state === 'active'
                    ? `${word.length}-letter word starting with ${word[0]}`
                    : word
                }
              >
                {word.split('').map((_, position) => {
                  // A word still to come shows its first letter and nothing
                  // else; only the active row reads the typed letters, so a
                  // pending row can never borrow one.
                  const given = isRevealed(index, position);
                  const letter =
                    state === 'given' || state === 'solved'
                      ? word[position]
                      : state === 'active'
                      ? slotLetter(index, position)
                      : given
                      ? word[position]
                      : '';
                  const free = given && state !== 'given' && state !== 'solved';
                  return (
                    <div
                      key={position}
                      className={
                        'wc-tile' +
                        (free ? ' is-given' : '') +
                        (letter && state === 'active' && !given ? ' is-typed' : '')
                      }
                    >
                      {letter}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {toast && <div className="wc-toast">{toast}</div>}

      <Keyboard
        onLetter={onLetter}
        onDelete={onDelete}
        onSubmit={submit}
        submitEnabled={outcome === null && typed.length === openSlots.length}
        onHint={useHint}
        hintEnabled={outcome === null && !hintUsed && activeIndex !== null}
      />

      {outcome === 'won' && (
        <DailyDoneOverlay
          dateKey={dateKey}
          isToday={isToday}
          subtitle={
            mistakes === 0
              ? `Chained all ${puzzle.words.length} words without a mistake.`
              : `Chained all ${puzzle.words.length} words with ${mistakes} ${
                  mistakes === 1 ? 'mistake' : 'mistakes'
                }.`
          }
        />
      )}

      {outcome === 'lost' && (
        <div className="wc-overlay">
          <div className="wc-overlay-card">
            <div className="wc-overlay-emoji">😬</div>
            <div className="wc-overlay-title">Out of lives</div>
            <div className="wc-overlay-sub">The chain was</div>
            <div className="wc-overlay-answer">
              {puzzle.words.map((word, i) => (
                <span key={i} className="wc-overlay-word">
                  {word}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <GameShell
      gameId="wordChain"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${solvedCount}/${puzzle.words.length}`}
    >
      {body}
    </GameShell>
  );
}
